import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DateField} from '@/components/date-field'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {FieldError} from '@/components/field-error'
import {
  useDecidirPerfilContratista,
  useEscribirEmpresaContratada,
  useEscribirPerfilAMano,
  type PerfilContratista,
  type Procedencia,
} from '@/hooks/empresa/perfil_cargo/use-perfil-contratista'
import {useProveedores, type Proveedor} from '@/hooks/empresa/proveedores/use-proveedores'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** `formatDateOnly` espera una fecha sola: con el instante entero da `Invalid Date` y `Intl`
 * revienta la pestaña. */
export function elDia(stamp: string): string {
  return formatDateOnly(stamp.slice(0, 10))
}

/** La empresa sale del directorio de proveedores; el texto libre es solo para la que aún no
 * está, así la misma empresa no queda escrita de dos formas. */
export function EmpresaContratada({
  businessId,
  employeeId,
  hoja,
  canOperate,
}: {
  businessId: string
  employeeId: string
  hoja: PerfilContratista
  canOperate: boolean
}) {
  const {t} = useTranslation()
  const escribir = useEscribirEmpresaContratada(businessId, employeeId)
  const [busqueda, setBusqueda] = useState('')
  const proveedores = useProveedores(canOperate ? businessId : undefined, {
    page: 1,
    search: busqueda,
    sortBy: 'razon_social',
    sortDir: 'asc',
  })
  const [proveedor, setProveedor] = useState<Proveedor | null>(null)
  const [nombre, setNombre] = useState('')
  const [invalido, setInvalido] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  function submit(evento: FormEvent) {
    evento.preventDefault()
    const fallo = proveedor ? null : validateRequired(nombre)
    setInvalido(fallo)
    setError(null)
    if (fallo) return
    escribir.mutate(
      {proveedorId: proveedor?.id ?? null, empresaNombre: proveedor ? null : nombre.trim()},
      {
        onSuccess: () => {
          setNombre('')
          setProveedor(null)
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <div className="space-y-2" data-testid="perfil-contratista-empresa">
      <p className="text-sm">
        {hoja.empresa_contratada
          ? t('perfilContratista.empresa.escrita', {
              empresa: hoja.empresa_contratada,
              nombre: hoja.empresa_escrita_por_nombre ?? '',
              fecha: hoja.empresa_escrita_en ? elDia(hoja.empresa_escrita_en) : '',
            })
          : t('perfilContratista.empresa.sinEscribir')}
      </p>
      {canOperate && (
        <form className="space-y-2" noValidate onSubmit={submit}>
          <Label htmlFor="perfil_contratista_proveedor">{t('perfilContratista.empresa.label')}</Label>
          <EntitySearchSelect<Proveedor>
            id="perfil_contratista_proveedor"
            items={proveedores.data?.items ?? []}
            value={proveedor}
            getKey={(fila) => fila.id}
            filterText={(fila) => fila.razon_social}
            renderOption={(fila) => fila.razon_social}
            renderTriggerValue={(fila) => fila.razon_social}
            placeholder={t('perfilContratista.empresa.delDirectorio')}
            searchPlaceholder={t('perfilContratista.empresa.buscar')}
            noResultsLabel={t('perfilContratista.empresa.sinProveedores')}
            clearLabel={t('perfilContratista.empresa.aMano')}
            testId="perfil-contratista-proveedor"
            onQueryChange={setBusqueda}
            onPick={setProveedor}
          />
          <Label htmlFor="perfil_contratista_empresa">{t('perfilContratista.empresa.escribirla')}</Label>
          <Input
            maxLength={tope('RAZ')}
            id="perfil_contratista_empresa"
            value={nombre}
            disabled={!!proveedor}
            data-testid="perfil-contratista-empresa-nombre"
            onBlur={() => setInvalido(proveedor ? null : validateRequired(nombre))}
            onChange={(evento) => setNombre(evento.target.value)}
          />
          <FieldError error={invalido ?? undefined} />
          <p className="text-xs text-muted-foreground">{t('perfilContratista.empresa.ayuda')}</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button
            size="sm"
            type="submit"
            disabled={escribir.isPending}
            data-testid="perfil-contratista-empresa-guardar">
            {t('perfilContratista.empresa.guardar')}
          </Button>
        </form>
      )}
    </div>
  )
}

/** Autor y fecha los declara la persona: Kairos no infiere que la empresa contratada es la autora. */
export function ProcedenciaDeclarada({
  autor,
  fecha,
  onAutor,
  onFecha,
}: {
  autor: string
  fecha: string
  onAutor: (valor: string) => void
  onFecha: (valor: string) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-2">
      <Label htmlFor="perfil_contratista_autor">{t('perfilContratista.documento.autorLabel')}</Label>
      <Input
        maxLength={tope('NOTA')}
        id="perfil_contratista_autor"
        value={autor}
        data-testid="perfil-contratista-autor"
        onChange={(evento) => onAutor(evento.target.value)}
      />
      <p className="text-xs text-muted-foreground">{t('perfilContratista.documento.autorAyuda')}</p>
      <DateField
        id="perfil_contratista_fecha"
        label={t('perfilContratista.documento.fechaLabel')}
        value={fecha}
        hint={t('perfilContratista.documento.fechaAyuda')}
        onChange={onFecha}
      />
    </div>
  )
}

/** Se pinta según `cargado_en`, no `id`: el mint crea la fila antes de que exista el archivo. */
export function Documento({hoja}: {hoja: PerfilContratista}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-1" data-testid="perfil-contratista-documento">
      {hoja.download_url && (
        <a className="text-sm underline" href={hoja.download_url} target="_blank" rel="noreferrer">
          {hoja.filename}
        </a>
      )}
      {hoja.lectura_ilegible && (
        <p className="text-sm" data-testid="perfil-contratista-ilegible">
          {t(hoja.lector_disponible ? 'perfilContratista.ilegible' : 'perfilContratista.sinLector.guardado')}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {t('perfilContratista.documento.procedencia', {
          autor: hoja.autor_declarado ?? t('perfilContratista.documento.autorDesconocido'),
          nombre: hoja.cargado_por_nombre ?? '',
          fecha: hoja.cargado_en ? elDia(hoja.cargado_en) : '',
        })}
      </p>
      {hoja.fecha_declarada && (
        <p className="text-xs text-muted-foreground" data-testid="perfil-contratista-fechado">
          {t('perfilContratista.documento.fechado', {fecha: formatDateOnly(hoja.fecha_declarada)})}
        </p>
      )}
      {hoja.estado && (
        <p className="text-xs text-muted-foreground" data-testid="perfil-contratista-estado">
          {t(`perfilContratista.estado.${hoja.estado}`, {
            motivo: hoja.motivo_devolucion ?? '',
            nombre: hoja.decidido_por_nombre ?? '',
            fecha: hoja.decidido_en ? elDia(hoja.decidido_en) : '',
          })}
        </p>
      )}
    </div>
  )
}

export function EscribirAMano({
  businessId,
  employeeId,
  hoja,
  procedencia,
  onListo,
}: {
  businessId: string
  employeeId: string
  hoja: PerfilContratista
  procedencia: Procedencia
  onListo: () => void
}) {
  const {t} = useTranslation()
  const escribir = useEscribirPerfilAMano(businessId, employeeId)
  const [peligros, setPeligros] = useState('')
  const [funciones, setFunciones] = useState('')
  const [invalido, setInvalido] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  function guardar() {
    setConfirmando(false)
    escribir.mutate(
      {
        ...procedencia,
        lineas: [
          ...renglones(peligros).map((texto) => ({tipo: 'peligro' as const, texto})),
          ...renglones(funciones).map((texto) => ({tipo: 'funcion' as const, texto})),
        ],
      },
      {onSuccess: onListo, onError: (err) => setError(translateApiError(err))}
    )
  }

  function submit(evento: FormEvent) {
    evento.preventDefault()
    const fallo = validateRequired(`${peligros}${funciones}`.trim())
    setInvalido(fallo)
    setError(null)
    if (fallo) return
    if (hoja.lineas.length > 0) return setConfirmando(true)
    guardar()
  }

  return (
    <form className="space-y-3" noValidate onSubmit={submit} data-testid="perfil-contratista-a-mano">
      <div className="space-y-1">
        <Label htmlFor="perfil_contratista_peligros">{t('perfilContratista.aMano.peligros')}</Label>
        <Textarea
          maxLength={tope('TEXTO')}
          id="perfil_contratista_peligros"
          value={peligros}
          data-testid="perfil-contratista-peligros"
          onChange={(evento) => setPeligros(evento.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="perfil_contratista_funciones">{t('perfilContratista.aMano.funciones')}</Label>
        <Textarea
          maxLength={tope('TEXTO')}
          id="perfil_contratista_funciones"
          value={funciones}
          data-testid="perfil-contratista-funciones"
          onChange={(evento) => setFunciones(evento.target.value)}
        />
      </div>
      <FieldError error={invalido ?? undefined} />
      <p className="text-xs text-muted-foreground">{t('perfilContratista.aMano.ayuda')}</p>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button size="sm" type="submit" disabled={escribir.isPending} data-testid="perfil-contratista-a-mano-guardar">
        {t('perfilContratista.aMano.guardar')}
      </Button>
      <ConfirmDialog
        open={confirmando}
        onOpenChange={setConfirmando}
        title={t('perfilContratista.reemplazo.titulo')}
        description={t('perfilContratista.reemplazo.cuerpo', argsDelReemplazo(hoja))}
        porque={porqueDelReemplazo(t)}
        confirmLabel={t('perfilContratista.reemplazo.confirmar')}
        cancelLabel={t('common.cancel')}
        pending={escribir.isPending}
        onConfirm={guardar}
      />
    </form>
  )
}

export function argsDelReemplazo(hoja: PerfilContratista) {
  return {
    total: hoja.lineas.length,
    confirmadas: hoja.lineas.filter((linea) => linea.confirmado).length,
  }
}

export function porqueDelReemplazo(t: (clave: string) => string) {
  return {
    label: t('perfilContratista.reemplazo.porqueLabel'),
    testId: 'perfil-contratista-reemplazo-porque',
    contenidoTestId: 'perfil-contratista-reemplazo-porque-contenido',
    contenido: <p>{t('perfilContratista.reemplazo.porqueBody')}</p>,
  }
}

function renglones(texto: string): string[] {
  return texto
    .split('\n')
    .map((renglon) => renglon.trim())
    .filter(Boolean)
}

/** Aceptar o devolver no toca líneas: el contenido solo lo cambia el empleador. Devolver no
 * exige listas (§4); aceptar sí. */
export function Decision({
  businessId,
  employeeId,
  hoja,
  canFirmar,
  razonBloqueoFirma,
}: {
  businessId: string
  employeeId: string
  hoja: PerfilContratista
  canFirmar: boolean
  razonBloqueoFirma: string | null
}) {
  const {t} = useTranslation()
  const decidir = useDecidirPerfilContratista(businessId, employeeId)
  const [motivo, setMotivo] = useState('')
  const [invalido, setInvalido] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  if (!canFirmar) {
    return razonBloqueoFirma ? (
      <p className="text-xs text-muted-foreground" data-testid="perfil-contratista-sin-firma">
        {razonBloqueoFirma}
      </p>
    ) : null
  }

  function devolver() {
    setConfirmando(false)
    decidir.mutate(
      {decision: 'devolver', motivo: motivo.trim()},
      {onSuccess: () => setMotivo(''), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <form
      className="space-y-2"
      noValidate
      data-testid="perfil-contratista-decision"
      onSubmit={(evento) => {
        evento.preventDefault()
        const fallo = validateRequired(motivo)
        setInvalido(fallo)
        setError(null)
        if (fallo) return
        setConfirmando(true)
      }}>
      <Button
        size="sm"
        type="button"
        variant="outline"
        disabled={decidir.isPending || hoja.estado === 'aceptado' || hoja.lineas.length === 0}
        title={hoja.lineas.length === 0 ? t('perfilContratista.decision.sinListas') : undefined}
        data-testid="perfil-contratista-aceptar"
        onClick={() => {
          setError(null)
          decidir.mutate({decision: 'aceptar'}, {onError: (err) => setError(translateApiError(err))})
        }}>
        {t('perfilContratista.decision.aceptar')}
      </Button>
      {hoja.lineas.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="perfil-contratista-sin-listas">
          {t('perfilContratista.decision.sinListas')}
        </p>
      )}
      <Label htmlFor="perfil_contratista_motivo">{t('perfilContratista.decision.motivo')}</Label>
      <Textarea
        maxLength={tope('NOTA')}
        id="perfil_contratista_motivo"
        value={motivo}
        data-testid="perfil-contratista-motivo"
        onBlur={() => setInvalido(validateRequired(motivo))}
        onChange={(evento) => setMotivo(evento.target.value)}
      />
      <FieldError error={invalido ?? undefined} />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button
        size="sm"
        type="submit"
        variant="ghost"
        disabled={decidir.isPending}
        data-testid="perfil-contratista-devolver">
        {t('perfilContratista.decision.devolver')}
      </Button>
      <ConfirmDialog
        open={confirmando}
        onOpenChange={setConfirmando}
        title={t('perfilContratista.decision.devolverTitulo', {
          empresa: hoja.empresa_contratada ?? t('perfilContratista.documento.autorDesconocido'),
        })}
        description={t('perfilContratista.decision.devolverCuerpo')}
        porque={{
          label: t('perfilContratista.decision.devolverPorqueLabel'),
          testId: 'perfil-contratista-devolver-porque',
          contenidoTestId: 'perfil-contratista-devolver-porque-contenido',
          contenido: <p>{t('perfilContratista.decision.devolverPorqueBody')}</p>,
        }}
        confirmLabel={t('perfilContratista.decision.devolver')}
        cancelLabel={t('common.cancel')}
        pending={decidir.isPending}
        onConfirm={devolver}
      />
    </form>
  )
}
